import { XIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/shared/lib'
import { FieldOutline } from '@/shared/ui/field-outline'
import { FloatingLabel } from '@/shared/ui/floating-label'

type TagsInputProps = {
  label: string
  value: string[]
  onChange: (value: string[]) => void
  /** 1–3 words under the field: «Добавить и Enter». */
  hint?: ReactNode
  /** Shown while the field holds nothing at all. */
  placeholder?: string
  /** Tags are identifiers by default — DNS names, UTM keys, source names. */
  isMono?: boolean
  className?: string
}

/** The same recipe `MultiSelect` uses, so both chips-in-a-field read as one control. */
const CHIP =
  'inline-flex h-6 shrink-0 items-center gap-1.25 rounded-full border border-primary/26 bg-primary/14 text-caption text-accent-text'

/** Comma, semicolon, whitespace — every list separator a clipboard is likely to carry. */
const SEPARATORS = /[\s,;]+/

/**
 * Free text as chips, typed into the field rather than listed under it.
 *
 * `MultiSelect` is the same shape for a closed option list; this one takes
 * whatever is typed, which is why there is no popup and no filter. Unlike that
 * control it wraps: a tag list has no natural length, and folding the tail into
 * a `+N` would hide values nothing else on the screen repeats. The field grows
 * downwards, so a row of them needs `items-start`.
 *
 * The label is raised permanently — chips are content, and a centred label over
 * them would read as one of the values.
 */
export function TagsInput({
  label,
  value,
  onChange,
  hint,
  placeholder,
  isMono = true,
  className,
}: TagsInputProps) {
  const { t } = useTranslation('common')
  const id = useId()
  const hintId = `${id}-hint`
  const inputRef = useRef<HTMLInputElement>(null)
  const [draft, setDraft] = useState('')

  /** Blanks and duplicates are dropped, both against the list and within the batch. */
  const add = (tags: string[]) => {
    const next = [...value]
    for (const tag of tags) {
      const trimmed = tag.trim()
      if (trimmed !== '' && !next.includes(trimmed)) next.push(trimmed)
    }
    if (next.length !== value.length) onChange(next)
    setDraft('')
  }

  const remove = (tag: string) => onChange(value.filter((item) => item !== tag))

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className="group/field relative">
        {/* Clicking the padding is clicking the field — the input is only the
            tail of it once a few chips are in. */}
        <div
          className="relative flex min-h-control-lg w-full cursor-text flex-wrap items-center gap-1.5 rounded-lg bg-surface-sunken px-2 py-1.5 transition-colors"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              event.preventDefault()
              inputRef.current?.focus()
            }
          }}
        >
          <FloatingLabel isRaised htmlFor={id}>
            {label}
          </FloatingLabel>

          {value.map((tag) => (
            <span key={tag} className={cn(CHIP, 'py-0 pr-1 pl-2.25', isMono && 'font-mono')}>
              {tag}
              <button
                type="button"
                aria-label={`${t('ui.remove')} ${tag}`}
                /* Without this the field blurs first, `commit` lands, and the
                 * removal is computed from a `value` one render out of date —
                 * the tag just typed disappears instead of this one. */
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => remove(tag)}
                className="flex size-4 items-center justify-center rounded-full outline-none hover:bg-primary/20"
              >
                <XIcon strokeWidth={2} className="size-2" aria-hidden />
              </button>
            </span>
          ))}

          <input
            id={id}
            ref={inputRef}
            value={draft}
            aria-label={label}
            aria-describedby={hint === undefined ? undefined : hintId}
            placeholder={value.length > 0 ? '' : placeholder}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ',') {
                event.preventDefault()
                add([draft])
                return
              }
              // An empty draft means Backspace has nothing of its own to eat.
              const last = value[value.length - 1]
              if (event.key === 'Backspace' && draft === '' && last !== undefined) {
                remove(last)
              }
            }}
            onPaste={(event) => {
              const text = event.clipboardData.getData('text')
              if (!SEPARATORS.test(text)) return
              event.preventDefault()
              add(text.split(SEPARATORS))
            }}
            onBlur={() => add([draft])}
            className={cn(
              'h-6 min-w-12 flex-1 bg-transparent pl-1 text-body outline-none placeholder:text-fg-disabled',
              isMono && 'font-mono',
            )}
          />
        </div>

        <FieldOutline label={label} isRaised className="border-border-strong" />
      </div>

      {hint !== undefined && (
        <div id={hintId} className="text-caption text-muted-foreground">
          {hint}
        </div>
      )}
    </div>
  )
}
